import { isExperimentPreviewMode } from "../experiments/previewMode";
import { safeLocalStorage } from "../../utils/safeStorage";
import callCloudRunFunction from "../cloudRunServices";
import {
  buildFunnelDedupeKey,
  buildFunnelEventPayload,
  isTrustedFunnelConversionEventName,
} from "./funnelSchema";
import { isExperienceEventName } from "../experience/experienceTelemetryRegistry";
import { getCoordinatedActivitySession } from "../session/activitySessionIdentity";

const ONCE_STORAGE_KEY = "funnelEvents:v1:once";
const DURABLE_LANDING_STORAGE_KEY = "funnelEvents:v1:durableLanding";
const DURABLE_LANDING_MAX_AGE_MS = 48 * 60 * 60 * 1000;
const DURABLE_LANDING_MAX_ITEMS = 20;
const FLUSH_DELAY_MS = 5000;
const MAX_BATCH_SIZE = 10;
const MAX_REQUEST_BYTES = 180 * 1024;
const AUTH_PENDING_MAX_ITEMS = 50;
const AUTH_PENDING_MAX_BYTES = 128 * 1024;
const AUTH_PENDING_MAX_AGE_MS = 30 * 1000;
const ACTIVITY_PREPARATION_MAX_ITEMS = 50;
const ACTIVITY_PREPARATION_MAX_BYTES = 128 * 1024;
const ACTIVITY_PREPARATION_MAX_AGE_MS = 5 * 1000;
const PASSIVE_ACTIVITY_EVENT_NAMES = new Set([
  "experience_step_dwell_summary",
  "landing_engagement_milestone",
  "landing_section_dwell_summary",
]);
const NO_ACTIVITY_PREPARATION_EVENT_NAMES = new Set(["share_hub_dwell_summary"]);
const NON_BLOCKING_PRE_ONBOARDING_FAILURE_CODES = new Set([
  "address_invalid",
  "email_exists",
  "email_invalid",
  "phone_exists",
  "phone_invalid",
]);

let queue = [];
let flushTimer = null;
let flushPromise = null;
let authPendingTimer = null;
let activityPreparationTimer = null;
let sequence = 0;
let durableQueueHydrated = false;
const pendingOnceKeys = new Set();
const authPendingDiagnostics = {
  deferred: 0,
  resolved: 0,
  timeoutFallback: 0,
  overflowFallback: 0,
  preparationFallback: 0,
};

export const isFunnelPreviewTelemetryDisabled = () => isExperimentPreviewMode();

const shouldSkipFunnelTelemetry = ({ allowInTests = false } = {}) =>
  (!allowInTests && process.env.NODE_ENV === "test") ||
  isFunnelPreviewTelemetryDisabled();

const isForegroundDocument = () =>
  typeof document === "undefined" || document.visibilityState !== "hidden";

const isQualifyingForegroundActivity = (
  input = {},
  { qualifyingActivity = undefined } = {},
) => {
  if (!isForegroundDocument()) return false;
  if (qualifyingActivity === false) return false;
  const eventName = String(input.eventName || "").trim();
  const eventType = String(input.eventType || "")
    .trim()
    .toLowerCase();
  if (
    eventType === "summary" ||
    PASSIVE_ACTIVITY_EVENT_NAMES.has(eventName) ||
    eventName.endsWith("_dwell_summary")
  ) {
    return false;
  }
  if (qualifyingActivity === true) return true;
  return ["action", "step", "validation", "view"].includes(eventType);
};

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getOnceMap = () => {
  const parsed = safeParse(safeLocalStorage.getItem(ONCE_STORAGE_KEY) || "");
  return parsed && typeof parsed === "object" ? parsed : {};
};

const setOnceMap = (value = {}) => {
  try {
    const keys = Object.keys(value);
    if (keys.length > 1500) {
      keys.sort((a, b) => (value[a] || 0) - (value[b] || 0));
      keys.slice(0, keys.length - 1500).forEach((key) => delete value[key]);
    }
    safeLocalStorage.setItem(ONCE_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Analytics idempotency should not block app usage.
  }
};

const isDurableFunnelEvent = (event = {}) => {
  if (event.eventName === "landing_viewed") return true;
  if (event.eventName === "share_hub_dwell_summary") return true;
  if (event.eventName === "account_access_failed") return true;
  if (event.eventName !== "pre_onboarding_submit_failed") return false;
  const errorCode = String(event.properties?.errorCode || "")
    .trim()
    .toLowerCase();
  return !NON_BLOCKING_PRE_ONBOARDING_FAILURE_CODES.has(errorCode);
};

const getDurableLandingItems = () => {
  const parsed = safeParse(
    safeLocalStorage.getItem(DURABLE_LANDING_STORAGE_KEY) || "",
  );
  if (!Array.isArray(parsed)) return [];
  const cutoff = Date.now() - DURABLE_LANDING_MAX_AGE_MS;
  return parsed
    .filter(
      (item) =>
        isDurableFunnelEvent(item?.event) &&
        Number(item.persistedAtMs || 0) >= cutoff,
    )
    .slice(-DURABLE_LANDING_MAX_ITEMS);
};

const setDurableLandingItems = (items = []) => {
  try {
    safeLocalStorage.setItem(
      DURABLE_LANDING_STORAGE_KEY,
      JSON.stringify(items.slice(-DURABLE_LANDING_MAX_ITEMS)),
    );
  } catch {
    // Durable telemetry retry is best effort when browser storage is blocked.
  }
};

const hydrateDurableLandingQueue = () => {
  if (durableQueueHydrated) return;
  durableQueueHydrated = true;
  const existingIds = new Set(
    queue.map((item) => item.event?.clientEventId).filter(Boolean),
  );
  getDurableLandingItems().forEach((item) => {
    if (!existingIds.has(item.event?.clientEventId)) {
      queue.push({ event: item.event, dedupeKey: item.dedupeKey || null });
      if (item.dedupeKey) pendingOnceKeys.add(item.dedupeKey);
    }
  });
};

const persistDurableLandingItem = (item) => {
  if (!isDurableFunnelEvent(item?.event)) return;
  const items = getDurableLandingItems().filter(
    (stored) => stored.event?.clientEventId !== item.event?.clientEventId,
  );
  items.push({
    event: item.event,
    dedupeKey: item.dedupeKey || null,
    persistedAtMs: Date.now(),
  });
  setDurableLandingItems(items);
};

const removeDurableLandingItem = (item) => {
  if (!isDurableFunnelEvent(item?.event)) return;
  setDurableLandingItems(
    getDurableLandingItems().filter(
      (stored) => stored.event?.clientEventId !== item.event?.clientEventId,
    ),
  );
};

const generateClientEventId = () => {
  const randomId =
    typeof window !== "undefined" && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  sequence += 1;
  return `${sequence}_${randomId}`;
};

const sendTelemetryBatch = (
  payload,
  { keepalive = false, includeAuthToken = false } = {},
) =>
  callCloudRunFunction(
    "funnel-telemetry-handler",
    "trackFunnelTelemetry",
    payload,
    {
      requiresAuth: false,
      includeAuthToken,
      retry: keepalive ? { retries: 0 } : { retries: 1, backoffMs: 500 },
      fetchOptions: keepalive ? { keepalive: true } : {},
    },
  );

const scheduleFlush = () => {
  if (typeof window === "undefined") return;
  if (flushTimer) return;
  flushTimer = window.setTimeout(() => {
    flushFunnelEvents();
  }, FLUSH_DELAY_MS);
};

const markPersisted = (item) => {
  removeDurableLandingItem(item);
  if (!item.dedupeKey) return;
  const onceMap = getOnceMap();
  onceMap[item.dedupeKey] = Date.now();
  setOnceMap(onceMap);
  pendingOnceKeys.delete(item.dedupeKey);
};

const releaseOnce = (item, { discardDurable = false } = {}) => {
  if (discardDurable) removeDurableLandingItem(item);
  if (item.dedupeKey) pendingOnceKeys.delete(item.dedupeKey);
};

const retainOnce = (item) => {
  if (item.dedupeKey) pendingOnceKeys.add(item.dedupeKey);
};

const isRejectedItem = (item, rejectedEvents = []) =>
  rejectedEvents.some(
    (rejection) =>
      rejection?.clientEventId === item.event.clientEventId ||
      rejection?.dedupeKey === item.event.dedupeKey ||
      rejection?.index === item.batchIndex,
  );

const compactTelemetryError = (error) => ({
  ok: false,
  message: String(error?.message || error || "Telemetry request failed"),
  httpStatus: error?.httpStatus || null,
  endpoint: error?.endpoint || null,
  requestId: error?.requestId || null,
});

const withoutBatchIndex = ({ batchIndex, ...item }) => item;

const estimateJsonBytes = (value) => {
  try {
    const json = JSON.stringify(value);
    if (typeof TextEncoder !== "undefined") {
      return new TextEncoder().encode(json).length;
    }
    return json.length * 2;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
};

const estimateEventPayloadBytes = (items = []) =>
  estimateJsonBytes({ events: items.map((item) => item.event) });

const getAuthPendingItems = () =>
  queue.filter((item) => item.authPending === true);

const getReadyItems = () =>
  queue.filter(
    (item) =>
      item.authPending !== true && item.preparingActivitySession !== true,
  );

const getPreparingItems = () =>
  queue.filter((item) => item.preparingActivitySession === true);

const releaseActivityPreparation = (item) => {
  item.preparingActivitySession = false;
  item.activityPreparationReleased = true;
  item.activityPreparationUntilMs = null;
  item.event = {
    ...item.event,
    activitySessionId: null,
    activitySessionQualified: false,
  };
  if (typeof item.resolveActivityPreparationFallback === "function") {
    item.resolveActivityPreparationFallback(null);
    item.resolveActivityPreparationFallback = null;
  }
};

const releasePreparingFunnelEvents = () => {
  let released = 0;
  queue.forEach((item) => {
    if (item.preparingActivitySession !== true) return;
    releaseActivityPreparation(item);
    released += 1;
  });
  if (released) {
    authPendingDiagnostics.preparationFallback += released;
  }
  scheduleActivityPreparationFallback();
  return released;
};

const clearActivityPreparationTimer = () => {
  if (activityPreparationTimer && typeof window !== "undefined") {
    window.clearTimeout(activityPreparationTimer);
  }
  activityPreparationTimer = null;
};

const scheduleActivityPreparationFallback = () => {
  clearActivityPreparationTimer();
  if (typeof window === "undefined") return;
  const preparingItems = getPreparingItems();
  if (!preparingItems.length) return;
  const nextExpiryMs = Math.min(
    ...preparingItems.map((item) =>
      Number(item.activityPreparationUntilMs || 0),
    ),
  );
  activityPreparationTimer = window.setTimeout(
    () => {
      activityPreparationTimer = null;
      releaseExpiredActivityPreparations();
    },
    Math.max(0, nextExpiryMs - Date.now()),
  );
};

const releaseExpiredActivityPreparations = (nowMs = Date.now()) => {
  let released = 0;
  queue.forEach((item) => {
    if (
      item.preparingActivitySession === true &&
      Number(item.activityPreparationUntilMs || 0) <= nowMs
    ) {
      releaseActivityPreparation(item);
      released += 1;
    }
  });
  if (released) {
    authPendingDiagnostics.preparationFallback += released;
    if (getReadyItems().length) scheduleFlush();
  }
  scheduleActivityPreparationFallback();
  return released;
};

const canPrepareActivitySession = (item) => {
  const preparingItems = getPreparingItems();
  if (preparingItems.length >= ACTIVITY_PREPARATION_MAX_ITEMS) return false;
  return (
    estimateEventPayloadBytes([...preparingItems, item]) <=
    ACTIVITY_PREPARATION_MAX_BYTES
  );
};

const clearAuthPendingTimer = () => {
  if (authPendingTimer && typeof window !== "undefined") {
    window.clearTimeout(authPendingTimer);
  }
  authPendingTimer = null;
};

const scheduleAuthPendingFallback = () => {
  clearAuthPendingTimer();
  if (typeof window === "undefined") return;
  const pendingItems = getAuthPendingItems();
  if (!pendingItems.length) return;
  const nextExpiryMs = Math.min(
    ...pendingItems.map((item) => Number(item.authPendingUntilMs || 0)),
  );
  authPendingTimer = window.setTimeout(
    () => {
      authPendingTimer = null;
      releaseExpiredAuthPendingEvents();
    },
    Math.max(0, nextExpiryMs - Date.now()),
  );
};

const releaseExpiredAuthPendingEvents = (nowMs = Date.now()) => {
  let released = 0;
  queue.forEach((item) => {
    if (
      item.authPending === true &&
      Number(item.authPendingUntilMs || 0) <= nowMs
    ) {
      item.authPending = false;
      item.authPendingUntilMs = null;
      released += 1;
    }
  });
  if (released) {
    authPendingDiagnostics.timeoutFallback += released;
    scheduleFlush();
  }
  scheduleAuthPendingFallback();
  return released;
};

const canDeferForAuth = (item) => {
  const pendingItems = getAuthPendingItems();
  if (pendingItems.length >= AUTH_PENDING_MAX_ITEMS) return false;
  return (
    estimateEventPayloadBytes([...pendingItems, item]) <= AUTH_PENDING_MAX_BYTES
  );
};

export const resolveDeferredFunnelEventIdentity = (identity = {}) => {
  const hasResolvedUserId = Object.prototype.hasOwnProperty.call(
    identity,
    "userId",
  );
  const hasResolvedMemberId = Object.prototype.hasOwnProperty.call(identity, "memberId");
  const pendingItems = getAuthPendingItems();
  pendingItems.forEach((item) => {
    item.event = {
      ...item.event,
      userId: hasResolvedUserId
        ? identity.userId || null
        : item.event.userId || null,
      ...(hasResolvedMemberId && item.event.flow === "share_campaign"
        ? { memberId: identity.memberId || null }
        : {}),
    };
    item.authPending = false;
    item.authPendingUntilMs = null;
  });
  if (pendingItems.length) {
    authPendingDiagnostics.resolved += pendingItems.length;
    scheduleFlush();
  }
  scheduleAuthPendingFallback();
  return pendingItems.length;
};

const buildTelemetryEventChunks = (items = []) => {
  const chunks = [];
  const droppedItems = [];
  let current = [];

  const flushCurrent = () => {
    if (current.length) {
      chunks.push(current);
      current = [];
    }
  };

  items.forEach((item) => {
    const singleItemBytes = estimateEventPayloadBytes([item]);
    if (
      !Number.isFinite(singleItemBytes) ||
      singleItemBytes > MAX_REQUEST_BYTES
    ) {
      droppedItems.push({
        ...item,
        reason: "telemetry-payload-too-large",
        estimatedBytes: Number.isFinite(singleItemBytes)
          ? singleItemBytes
          : null,
      });
      return;
    }

    const candidate = [...current, item];
    const candidateTooLarge =
      estimateEventPayloadBytes(candidate) > MAX_REQUEST_BYTES;
    if (
      current.length &&
      (current.length >= MAX_BATCH_SIZE || candidateTooLarge)
    ) {
      flushCurrent();
    }
    current.push(item);
  });

  flushCurrent();
  return { chunks, droppedItems };
};

const buildDropSummary = (droppedItems = []) =>
  droppedItems.map((item) => ({
    clientEventId: item.event?.clientEventId || null,
    dedupeKey: item.dedupeKey || item.event?.dedupeKey || null,
    reason: item.reason || "telemetry-event-dropped",
    estimatedBytes: item.estimatedBytes || null,
  }));

const appendDropSummary = (response, droppedItems = []) => {
  if (!droppedItems.length) return response;
  const base = response || {
    ok: true,
    acceptedEvents: 0,
    rejectedEvents: [],
  };
  return {
    ...base,
    droppedEvents: [
      ...(Array.isArray(base.droppedEvents) ? base.droppedEvents : []),
      ...buildDropSummary(droppedItems),
    ],
  };
};

const isPermanentPayloadError = (error) => Number(error?.httpStatus) === 413;

const summarizeBatchResponses = (responses = []) => {
  if (responses.length === 1) {
    return responses[0];
  }

  return {
    ok: true,
    batches: responses.length,
    acceptedEvents: responses.reduce(
      (sum, response) => sum + Number(response?.acceptedEvents || 0),
      0,
    ),
    rejectedEvents: responses.flatMap((response) =>
      Array.isArray(response?.rejectedEvents) ? response.rejectedEvents : [],
    ),
  };
};

const performFunnelEventsFlush = async ({
  keepalive = false,
  throwOnError = false,
} = {}) => {
  hydrateDurableLandingQueue();
  releaseExpiredAuthPendingEvents();
  if (flushTimer) {
    if (typeof window !== "undefined") {
      window.clearTimeout(flushTimer);
    }
    flushTimer = null;
  }

  const readyItems = getReadyItems();
  if (!readyItems.length) return null;

  const items = readyItems.map((item, batchIndex) => ({
    ...item,
    batchIndex,
  }));
  queue = queue.filter(
    (item) =>
      item.authPending === true || item.preparingActivitySession === true,
  );

  const publicItems = items.filter(
    (item) => !isExperienceEventName(item.event?.eventName),
  );
  const experienceItems = items.filter((item) =>
    isExperienceEventName(item.event?.eventName),
  );
  const publicBatch = buildTelemetryEventChunks(publicItems);
  const experienceBatch = buildTelemetryEventChunks(experienceItems);
  const chunks = [
    ...publicBatch.chunks.map((chunk) => ({
      // Authentication is optional, but attaching it when available lets a
      // delayed durable landing complete the original account-session anchor.
      includeAuthToken: true,
      items: chunk,
    })),
    ...experienceBatch.chunks.map((chunk) => ({
      includeAuthToken: true,
      items: chunk,
    })),
  ];
  const droppedItems = [
    ...publicBatch.droppedItems,
    ...experienceBatch.droppedItems,
  ];
  const responses = [];
  droppedItems.forEach((item) => releaseOnce(item, { discardDurable: true }));

  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
    const chunkConfig = chunks[chunkIndex];
    const chunk = chunkConfig.items.map((item, batchIndex) => ({
      ...item,
      batchIndex,
    }));

    try {
      const response = await sendTelemetryBatch(
        { events: chunk.map((item) => item.event) },
        {
          keepalive,
          includeAuthToken: chunkConfig.includeAuthToken,
        },
      );
      responses.push(response);
      const rejectedEvents = Array.isArray(response?.rejectedEvents)
        ? response.rejectedEvents
        : [];
      chunk.forEach((item) => {
        if (isRejectedItem(item, rejectedEvents)) {
          releaseOnce(item, { discardDurable: true });
          return;
        }
        markPersisted(item);
      });
    } catch (error) {
      if (isPermanentPayloadError(error)) {
        chunk.forEach((item) => releaseOnce(item, { discardDurable: true }));
        responses.push({
          ok: false,
          acceptedEvents: 0,
          rejectedEvents: buildDropSummary(
            chunk.map((item) => ({
              ...item,
              reason: "telemetry-payload-rejected",
              estimatedBytes: estimateEventPayloadBytes([item]),
            })),
          ),
        });
        continue;
      }
      const unsentItems = [
        ...chunk.map(withoutBatchIndex),
        ...chunks
          .slice(chunkIndex + 1)
          .flatMap((remainingChunk) => remainingChunk.items)
          .map(withoutBatchIndex),
      ];
      unsentItems.forEach(retainOnce);
      queue = [...unsentItems, ...queue];
      if (!keepalive) scheduleFlush();
      if (throwOnError) {
        throw error;
      }
      return {
        ...compactTelemetryError(error),
        batchesAcceptedBeforeFailure: responses.length,
        requeuedEvents: unsentItems.length,
        droppedEvents: buildDropSummary(droppedItems),
      };
    }
  }

  return appendDropSummary(summarizeBatchResponses(responses), droppedItems);
};

export const flushFunnelEvents = (options = {}) => {
  // A hot producer must not create parallel writes against the same Funnel
  // session/member documents. Work queued during this flight drains next.
  if (flushPromise) return flushPromise;

  const inFlight = (async () => {
    try {
      return await performFunnelEventsFlush(options);
    } finally {
      if (flushPromise === inFlight) {
        flushPromise = null;
        if (getReadyItems().length) scheduleFlush();
      }
    }
  })();
  flushPromise = inFlight;
  return inFlight;
};

export const updateFunnelSessionSummary = async (input = {}, options = {}) => {
  if (shouldSkipFunnelTelemetry(options)) return false;
  if (!input?.sessionId) return false;

  try {
    await sendTelemetryBatch(
      {
        sessions: [
          {
            ...input,
          },
        ],
      },
      { includeAuthToken: true },
    );
    return true;
  } catch (error) {
    if (options?.throwOnError === true) {
      throw error;
    }
    return false;
  }
};

const flushQueuedItemIfReady = async (item, { immediate = false, keepalive = false } = {}) => {
  if (item.authPending === true || item.preparingActivitySession === true) {
    return;
  }
  const readyItems = getReadyItems();
  if (immediate || readyItems.length >= MAX_BATCH_SIZE) {
    await flushFunnelEvents({ keepalive }).catch(() => null);
  } else if (estimateEventPayloadBytes(readyItems) >= MAX_REQUEST_BYTES) {
    await flushFunnelEvents().catch(() => null);
  } else {
    scheduleFlush();
  }
};

export const trackFunnelEvent = async (
  input = {},
  {
    immediate = false,
    keepalive = false,
    once = false,
    key = "",
    allowInTests = false,
    qualifyingActivity = undefined,
    deferUntilAuthReady = false,
    occurredAtClientForTests = "",
    activitySessionForTests = null,
  } = {},
) => {
  if (shouldSkipFunnelTelemetry({ allowInTests })) return null;
  if (typeof window === "undefined") return null;
  if (!input?.eventName) return null;
  if (isTrustedFunnelConversionEventName(input.eventName)) return null;
  hydrateDurableLandingQueue();

  const dedupeKey =
    input.dedupeKey ||
    (once
      ? buildFunnelDedupeKey({
          eventName: input.eventName,
          sessionId: input.sessionId,
          userId: input.userId,
          memberId: input.memberId,
          key,
        })
      : null);

  const onceMap = dedupeKey ? getOnceMap() : null;
  if (onceMap) {
    if (onceMap[dedupeKey]) return null;
    if (pendingOnceKeys.has(dedupeKey)) return null;
    pendingOnceKeys.add(dedupeKey);
  }

  const clientEventId = generateClientEventId();
  const occurredAtClient =
    allowInTests && occurredAtClientForTests
      ? new Date(occurredAtClientForTests).toISOString()
      : new Date().toISOString();
  const activitySessionQualified = isQualifyingForegroundActivity(input, {
    qualifyingActivity,
  });
  let event;
  try {
    event = buildFunnelEventPayload({
      ...input,
      activitySessionId: null,
      activitySessionQualified: false,
      clientEventId,
      dedupeKey,
      sequence,
      occurredAtClient,
    });
  } catch (error) {
    if (dedupeKey) pendingOnceKeys.delete(dedupeKey);
    throw error;
  }
  const item = {
    event,
    dedupeKey,
  };
  let activityPreparationFallback;
  if (!NO_ACTIVITY_PREPARATION_EVENT_NAMES.has(input.eventName) && canPrepareActivitySession(item)) {
    item.preparingActivitySession = true;
    item.activityPreparationUntilMs =
      Date.now() + ACTIVITY_PREPARATION_MAX_AGE_MS;
    activityPreparationFallback = new Promise((resolve) => {
      item.resolveActivityPreparationFallback = resolve;
    });
  } else {
    authPendingDiagnostics.preparationFallback += 1;
  }
  if (deferUntilAuthReady && canDeferForAuth(item)) {
    item.authPending = true;
    item.authPendingUntilMs = Date.now() + AUTH_PENDING_MAX_AGE_MS;
    authPendingDiagnostics.deferred += 1;
  } else if (deferUntilAuthReady) {
    authPendingDiagnostics.overflowFallback += 1;
  }
  queue.push(item);
  persistDurableLandingItem(item);
  scheduleAuthPendingFallback();
  scheduleActivityPreparationFallback();

  if (!item.preparingActivitySession) {
    await flushQueuedItemIfReady(item, { immediate, keepalive });
    return clientEventId;
  }

  let activitySession;
  try {
    const activitySessionPromise =
      allowInTests && activitySessionForTests
        ? Promise.resolve(activitySessionForTests)
        : getCoordinatedActivitySession({
            occurredAtMs: Date.parse(occurredAtClient),
            qualifyingActivity: activitySessionQualified,
            activitySessionOwnerId:
              input.sessionId || input.userId || input.memberId || "",
          });
    activitySession = await Promise.race([
      activitySessionPromise,
      activityPreparationFallback,
    ]);
  } catch (error) {
    const itemIndex = queue.indexOf(item);
    if (itemIndex < 0 || item.activityPreparationReleased) {
      return clientEventId;
    }
    queue.splice(itemIndex, 1);
    removeDurableLandingItem(item);
    if (dedupeKey) pendingOnceKeys.delete(dedupeKey);
    scheduleAuthPendingFallback();
    scheduleActivityPreparationFallback();
    throw error;
  }
  if (
    !activitySession ||
    !queue.includes(item) ||
    item.activityPreparationReleased
  ) {
    return clientEventId;
  }
  item.resolveActivityPreparationFallback = null;
  item.event = {
    ...item.event,
    activitySessionId: activitySession.activitySessionId,
    activitySessionQualified,
  };
  item.preparingActivitySession = false;
  item.activityPreparationUntilMs = null;
  persistDurableLandingItem(item);
  scheduleActivityPreparationFallback();
  await flushQueuedItemIfReady(item, { immediate, keepalive });

  return clientEventId;
};

export const _private = {
  resetForTests: ({ preserveDurableLanding = false } = {}) => {
    releasePreparingFunnelEvents();
    queue = [];
    flushPromise = null;
    pendingOnceKeys.clear();
    Object.keys(authPendingDiagnostics).forEach((key) => {
      authPendingDiagnostics[key] = 0;
    });
    sequence = 0;
    durableQueueHydrated = false;
    if (!preserveDurableLanding) {
      try {
        safeLocalStorage.removeItem(DURABLE_LANDING_STORAGE_KEY);
      } catch {
        // Test storage cleanup is best effort.
      }
    }
    if (flushTimer && typeof window !== "undefined") {
      window.clearTimeout(flushTimer);
    }
    flushTimer = null;
    clearAuthPendingTimer();
    clearActivityPreparationTimer();
  },
  buildTelemetryEventChunks,
  estimateEventPayloadBytes,
  shouldSkipFunnelTelemetry,
  getDurableLandingItems,
  hydrateDurableLandingQueue,
  isQualifyingForegroundActivity,
  getAuthPendingItems,
  getPreparingItems,
  getReadyItems,
  releasePreparingFunnelEvents,
  releaseExpiredActivityPreparations,
  releaseExpiredAuthPendingEvents,
  authPendingDiagnostics,
  AUTH_PENDING_MAX_ITEMS,
  AUTH_PENDING_MAX_BYTES,
  AUTH_PENDING_MAX_AGE_MS,
  ACTIVITY_PREPARATION_MAX_ITEMS,
  ACTIVITY_PREPARATION_MAX_BYTES,
  ACTIVITY_PREPARATION_MAX_AGE_MS,
};

if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      releasePreparingFunnelEvents();
      resolveDeferredFunnelEventIdentity();
      flushFunnelEvents({ keepalive: true }).catch(() => {});
    }
  });
  window.addEventListener("pagehide", () => {
    releasePreparingFunnelEvents();
    resolveDeferredFunnelEventIdentity();
    flushFunnelEvents({ keepalive: true }).catch(() => {});
  });
  window.addEventListener("online", () => {
    hydrateDurableLandingQueue();
    flushFunnelEvents().catch(() => {});
  });
}
